import { Mesh, MeshLayout } from './Mesh';
import { VertexBuffer } from './VertexBuffer';
import { Context } from '../context/Context';
import { Scene } from '../scene/Scene';
import { RenderObject } from '../object/RenderObject';
import { BoundingDimensions } from '../util';
import { getNumComponents, ResolvedPrimitive } from '../gltf';

export class GltfMesh extends Mesh {
  private get bounds(): BoundingDimensions {
    return this.localBounds;
  }

  public constructor(reference: string, ctx: Context, scene: Scene, primitive: ResolvedPrimitive) {
    super(reference, ctx, scene);

    const { interleaved, stride, layout } = this.interleaveAttributes(primitive);

    // Create GPU buffer
    const gl = ctx.gl;
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, interleaved, gl.STATIC_DRAW);

    this.buffer = new VertexBuffer(vbo, gl.TRIANGLES, stride, interleaved.length / stride);
    this.dataLayout = layout;
    this._localBounds = BoundingDimensions.createFromData(
      interleaved,
      interleaved.length / stride,
      layout.position!.offset,
      stride,
    );

    // Index buffer (optional). Unbind any currently-bound VAO before touching
    // ELEMENT_ARRAY_BUFFER — that target is VAO-encapsulated, so binding while
    // someone else's VAO is active silently rewrites their element binding and
    // breaks their next drawElements with "Insufficient buffer size".
    if (primitive.indices) {
      gl.bindVertexArray(null);
      const ibo = gl.createBuffer()!;
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, primitive.indices, gl.STATIC_DRAW);

      this.indexBuffer = new VertexBuffer(ibo, gl.TRIANGLES, 1, primitive.indices.length);
    }

    this.createVAO(gl);
  }

  public createInstance(): RenderObject {
    const object = new RenderObject(this);
    object.bounds = this.bounds.clone();
    object.bounds.bind(object.trs);
    return object;
  }

  private interleaveAttributes(primitive: ResolvedPrimitive): {
    interleaved: Float32Array;
    stride: number;
    layout: MeshLayout;
  } {
    const semanticMap: Record<string, keyof MeshLayout> = {
      POSITION: 'position',
      NORMAL: 'normal',
      COLOR_0: 'color',
      TEXCOORD_0: 'diffuseTexture',
      TEXCOORD_1: 'lightMap',
      JOINTS_0: 'jointIndices',
      WEIGHTS_0: 'jointWeights',
      _LIGHT: 'bakedLight',
    };

    const present = Object.entries(primitive.attributes)
      .filter(([semantic]) => semantic in semanticMap)
      .map(([semantic, { array, type }]) => ({
        semantic,
        layoutKey: semanticMap[semantic],
        array,
        length: getNumComponents(type),
      }));

    const count = present[0].array.length / present[0].length;
    const stride = present.reduce((sum, a) => sum + a.length, 0);

    const interleaved = new Float32Array(count * stride);
    const layout: MeshLayout = {};

    let offset = 0;
    for (const attr of present) {
      layout[attr.layoutKey] = { offset, length: attr.length };
      offset += attr.length;
    }

    for (let i = 0; i < count; i++) {
      let dst = i * stride;
      for (const { array, length } of present) {
        for (let k = 0; k < length; k++) {
          interleaved[dst++] = array[i * length + k];
        }
      }
    }
    return { interleaved, stride, layout };
  }
}
